import * as React from 'react';
import clsx from 'clsx';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { GradientModal } from '@/components/ui/GradientModal';
import { useUiStore } from '@/store/uiStore';

// Dumb components
import { GracePeriodCard } from './shared/GracePeriodCard';
import { DeleteWarningList } from './shared/DeleteWarningList';
import { DeleteInput } from './shared/DeleteInput';
import { DeleteModalHeader } from './shared/DeleteModalHeader';
import trashIcon from '@/assets/images/svg/delete_account_icon.svg';

/**
 * Modal to confirm permanent account deletion.
 * Requires user to type "DELETE" to proceed; non-OAuth users must also re-enter their password.
 */
export const DeleteAccountModal: React.FC = () => {
  const isOpen = useUiStore((state) => state.modals.deleteAccount);
  const closeModal = useUiStore((state) => state.closeModal);
  const { user, deleteAccount } = useAuth();

  const [confirmText, setConfirmText] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);

  const requiresPassword = !user?.oauth_provider;
  const isConfirmed = confirmText === 'DELETE' && (!requiresPassword || password.length > 0);

  const warningItems = [
    'Your account will be immediately deactivated and inaccessible',
    'All your digital echoes will be permanently deleted',
    'All conversation history and analytics will be lost',
    'Your personal information and preferences will be removed',
    'After 30 days, all account data will be permanently deleted from our systems',
  ];

  const handleConfirm = async () => {
    if (!isConfirmed) return;

    setIsProcessing(true);
    try {
      await deleteAccount(requiresPassword ? password : undefined);
      // If successful, close the modal. AuthProvider.logout will navigate.
      closeModal('deleteAccount');
    } catch {
      // Errors are surfaced by store; keep modal open and allow retry
      setIsProcessing(false);
    }
  };

  return (
    <GradientModal isOpen={isOpen} onClose={() => closeModal('deleteAccount')} variant='gray'>
      <DeleteModalHeader
        icon={trashIcon}
        title='Delete Your Account'
        subtitle='Are you sure you want to permanently delete your account?'
        description='This action will affect all your data and cannot be undone.'
      />

      <GracePeriodCard />

      <DeleteWarningList items={warningItems} />

      <DeleteInput value={confirmText} onChange={setConfirmText} />

      {requiresPassword && (
        <div className='mb-8 w-full'>
          <label className='mb-2 block font-sans text-16 font-bold text-brand-800'>
            Enter your current password
          </label>
          <p className='mb-2 font-sans text-14 font-normal text-brand-800'>
            Required to verify it&apos;s you.
          </p>
          <input
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder='Current password'
            autoComplete='current-password'
            className={clsx(
              'h-[48px] w-[300px] px-4',
              'rounded-10 border-1.5 border-delete-input',
              'bg-white',
              'font-sans text-16 font-semibold leading-160 tracking-[-0.02em]',
              'text-delete-input placeholder:text-delete-input',
              'focus:outline-none focus:ring-1 focus:ring-delete-input'
            )}
          />
        </div>
      )}

      <div className='mt-4 flex w-full flex-col items-center gap-4 md:flex-row md:justify-between'>
        <Button
          type='button'
          onClick={() => closeModal('deleteAccount')}
          variant='settings-modify'
          className='h-[44px] w-full px-8 text-16 text-brand-800 md:w-auto'
        >
          Keep My Account
        </Button>

        <Button
          onClick={handleConfirm}
          disabled={!isConfirmed || isProcessing}
          variant='text-destructive'
          size='auto'
          className='md:ml-auto'
        >
          {isProcessing ? 'DELETING...' : 'DELETE'}
        </Button>
      </div>
    </GradientModal>
  );
};
